// Shared mapping for `sections.opportunities.items` — used by the Opportunities
// tab and by the "Top hidden potentials" table in the Overview summary, so both
// render an opportunity the same way.

export interface OpportunityRow {
  title: string;
  page: string;
  score: number | null;
  competitorScore: number | null;
  evidence: string;
  reason: string;
  recommendation: string;
  confidenceLabel: string;
  confidenceClass: string;
  confidenceRank: number;
}

// The API grades each opportunity by `impact`; the table presents that as how
// much confidence the evidence supports, which is what the reader acts on.
const CONFIDENCE: {
  [k: string]: { label: string; class: string; rank: number };
} = {
  high: { label: 'High', class: 'high', rank: 3 },
  medium: { label: 'Medium', class: 'medium', rank: 2 },
  low: { label: 'Directional', class: 'low', rank: 1 },
};

export function mapOpportunityRows(
  items: any,
  compName: string,
): OpportunityRow[] {
  const list = Array.isArray(items) ? items : [];
  return list.map((o: any) => {
    const conf =
      CONFIDENCE[(o?.impact || '').toLowerCase()] || CONFIDENCE['low'];
    return {
      title: [o?.section, o?.category].filter(Boolean).join(' — '),
      page: o?.page || '',
      score: numOrNull(o?.score),
      competitorScore: numOrNull(o?.competitorScore),
      evidence: evidenceText(o, compName),
      reason: o?.reason || '',
      recommendation: o?.recommendation || '',
      confidenceLabel: conf.label,
      confidenceClass: conf.class,
      confidenceRank: conf.rank,
    };
  });
}

// "29 vs 61 (gonser.ch)" — the competitor half is dropped when untracked.
function evidenceText(o: any, compName: string): string {
  const score = numOrNull(o?.score);
  const comp = numOrNull(o?.competitorScore);
  if (score === null && comp === null) return '—';
  if (comp === null) return `${score}`;
  const suffix = compName ? ` (${compName})` : '';
  return `${score === null ? '—' : score} vs ${comp}${suffix}`;
}

function numOrNull(v: any): number | null {
  return typeof v === 'number' && !isNaN(v) ? Math.round(v) : null;
}
